import { Grid, LinearProgress } from "@material-ui/core";
import React, { useState, useRef, useCallback } from "react";
import { Button } from "react-bootstrap";
import RichTextField from "../../../../components/RichTextField/RichTextField";
import { CCP_STATES, MAX_EMAIL_SIZE_MB } from "../../../../utility/constants";
import { useCurrentContactState } from "../../hooks/useCurrentContactState";
import AttachmentItem from "./AttachmentItem";
import { ErrorOutlineOutlined as ErrorIcon } from "@material-ui/icons";
import { totalFilesSizeExceedLimit } from "../../../../utility/helpers";

const NewEmail = ({ classes, handleSendEmail, renderToastContent }) => {
	const [newEmailBody, setNewEmailBody] = useState("");
	const [sending, setSending] = useState(false);
	const [sizeLimitExceeded, setSizeLimitExceeded] = useState(false);
	const contactState = useCurrentContactState();

	const fileInputRef = useRef();
	const [selectedFiles, setSelectedFiles] = useState([]);

	const handleSend = async () => {
		setSending(true);
		await handleSendEmail(newEmailBody, selectedFiles);
		setNewEmailBody("");
		setSelectedFiles([]);
		setSending(false);
	};

	const handleFilesAdded = useCallback(
		(event) => {
			const { files } = event.target;

			// in case files are selected again after being updated
			// remove existing and add the new ones instead of ignoring it
			const fileNames = Array.from(files).map((file) => file.name);
			const existingFileNames = selectedFiles.map((file) => file.name);
			const willBeUpdated = existingFileNames.some((fileName) => fileNames.includes(fileName));

			let newFiles = [...selectedFiles].filter((file) => fileNames.includes(file.name) === false);
			newFiles = [...newFiles, ...files];
			setSelectedFiles(newFiles);

			// clear value to avoid duplicates when cancelling and re-selecting
			fileInputRef.current.value = "";

			if (willBeUpdated) {
				renderToastContent(true, "Attachments updated", "info");
			}

			setSizeLimitExceeded(totalFilesSizeExceedLimit(newFiles, MAX_EMAIL_SIZE_MB));
		},
		[selectedFiles, renderToastContent],
	);

	const handleDeleteAttachment = useCallback(
		(attachment) => {
			const newSelectedFiles = selectedFiles.filter((file) => file.name !== attachment.name);
			setSelectedFiles(newSelectedFiles);
			setSizeLimitExceeded(totalFilesSizeExceedLimit(newSelectedFiles, MAX_EMAIL_SIZE_MB));
		},
		[selectedFiles],
	);

	return (
		<div className={classes.inputContainer}>
			<input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesAdded} />

			{sizeLimitExceeded && (
				<div className={classes.sizeLimitWarning}>
					<ErrorIcon />
					<span>Message can not be sent, size limit of 30 MB exceeded.</span>
				</div>
			)}

			{selectedFiles.length > 0 && (
				<Grid container className={classes.attachmentsContainer}>
					{Array.from(selectedFiles).map((attachment, idx) => (
						<Grid key={idx} item xs={12} md={6} lg={4}>
							<AttachmentItem
								attachment={{ fileName: attachment.name }}
								editable
								handleDelete={() => handleDeleteAttachment(attachment)}
								classes={classes}
								disabled={sending}
							/>
						</Grid>
					))}
				</Grid>
			)}

			<RichTextField
				preserveHtmlLineBreaks
				height={324}
				value={newEmailBody}
				onChange={(val) => setNewEmailBody(val)}
				toolbarActions={[
					<Button
						size="sm"
						variant="outline-secondary"
						disabled={contactState !== CCP_STATES.contactStateTypes.CONNECTED}
						onClick={() => fileInputRef.current.click()}
						title="Attach"
					>
						<i className="fas fa-paperclip"></i>
					</Button>,
					<Button
						size="sm"
						variant="outline-secondary"
						disabled={!newEmailBody || sizeLimitExceeded || contactState !== CCP_STATES.contactStateTypes.CONNECTED}
						onClick={handleSend}
					>
						<i className="fas fa-paper-plane"></i> Send
					</Button>,
				]}
				disabled={sending || contactState !== CCP_STATES.contactStateTypes.CONNECTED}
				placeholder="Type your reply here"
			/>

			{sending && <LinearProgress className={classes.sendProgress} />}
		</div>
	);
};

export default NewEmail;
